import NiceModal, { useModal } from "@ebay/nice-modal-react";
import ModalManager from "@ebay/nice-modal-react";
import SideModal from "/src/components/modal/SideModal/SideModal";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import Form from "/src/components/form/Form";
import Field from "/src/components/UI/Field/Field";
import TextInput from "/src/components/UI/TextField/TextInput";
import TextArea from "/src/components/UI/TextField/TextArea";
import Button from "/src/components/UI/Button/Button";
import Select from "react-select";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import criteriaAdminService from "../../services/criteriaAdminService";
import "./CriteriaDefinitionModal.css";

const RESULT_OPTIONS = [
    { value: "empty", label: "Empty" },
    { value: "pass", label: "Pass" },
    { value: "fail", label: "Fail" },
];

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 5,
        borderWidth: 2,
        borderColor: state.isFocused ? "var(--main)" : "transparent",
        backgroundColor: "var(--white)",
        boxShadow: "none",
        "&:hover": { borderColor: "var(--main)" },
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
    menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden", zIndex: 20 }),
    singleValue: (base) => ({ ...base, color: "var(--dark)", fontWeight: 600, fontSize: 14 }),
    placeholder: (base) => ({ ...base, color: "var(--dark)", opacity: 0.5, fontSize: 14 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? "var(--main)" : state.isFocused ? "var(--light-secondary)" : "white",
        color: state.isSelected ? "white" : "var(--dark)",
        cursor: "pointer",
        fontSize: 14,
    }),
    indicatorSeparator: () => ({ display: "none" }),
};

const CriteriaDefinitionModal = NiceModal.create(({ title, data, criteriaType, categories = [] }) => {
    const modal = useModal();
    const { onClose } = useModalHelper(modal);
    const { createDefinition, updateDefinition } = criteriaAdminService;

    const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));

    const { register, handleSubmit, control, formState: { errors, isValid } } = useForm({
        mode: "onChange",
        defaultValues: {
            name: data?.name || "",
            description: data?.description || "",
            category_id: data?.category_id ?? null,
            color: data?.color || "#0e7aed",
            weight: data?.weight ?? 0,
            result: data?.result || "empty",
            trackers: (data?.trackers?.length ? data.trackers : [""]).map((value) => ({ value })),
        },
    });

    const { fields: trackerFields, append: appendTracker, remove: removeTracker } = useFieldArray({
        control,
        name: "trackers",
    });

    const onClickRemoveTracker = async (index) => {
        await ModalManager.show(ApiModal, {
            title: "Remove tracker",
            body: "Do you really want to remove this tracker?",
            buttons: [
                { className: "validate", label: "Cancel", style: { marginRight: "20px" } },
                { className: "cancel", label: "Remove", type: "submit" },
            ],
        }).then((confirmed) => {
            if (confirmed) removeTracker(index);
        });
    };

    const onSubmit = async (values) => {
        const payload = {
            ...values,
            weight: Number(values.weight),
            trackers: values.trackers.map((t) => t.value.trim()).filter(Boolean),
        };

        const [response, error] = data
            ? await updateDefinition(data.id, payload)
            : await createDefinition({ ...payload, criteria_type: criteriaType });

        if (error) {
            console.error(error);
            return;
        }
        modal.resolve(response);
        onClose();
    };

    return (
        <SideModal name="criteria-definition-modal" id={modal.id} autoClose="true" onClose={onClose} style={{ width: "550px" }}>
            <div className="button-leave" id="close_modal_container" onClick={() => onClose()}>
                <i id="close_modal" className="fa fa-times" aria-hidden="true"></i>
            </div>
            <Form className="criteria-form" onSubmit={handleSubmit(onSubmit)}>
                <Form.Title>{title}</Form.Title>
                <Form.Body className="criteria-form-body">

                    <div className="criteria-form-section">
                        <div className="criteria-form-section-title">
                            <i className="fas fa-circle-info"></i> General
                        </div>

                        <Field label={<span>Name <span className="obligatory_field">*</span></span>}>
                            <TextInput {...register("name", { required: true })} />
                            <span className="error-display">{errors.name ? "Name is required" : ""}</span>
                        </Field>

                        <Field label="Description">
                            <TextArea {...register("description")} style={{ height: "70px", width: "100%" }} />
                        </Field>

                        <Field label="Category">
                            <Controller
                                name="category_id"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        value={categoryOptions.find((o) => o.value === field.value) || null}
                                        options={categoryOptions}
                                        onChange={(option) => field.onChange(option ? option.value : null)}
                                        isClearable
                                        placeholder="No category"
                                        styles={selectStyles}
                                    />
                                )}
                            />
                        </Field>
                    </div>

                    <div className="criteria-form-section">
                        <div className="criteria-form-section-title">
                            <i className="fas fa-scale-balanced"></i> Scoring
                        </div>

                        <Field label="Default result">
                            <Controller
                                name="result"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        value={RESULT_OPTIONS.find((o) => o.value === field.value)}
                                        options={RESULT_OPTIONS}
                                        onChange={(option) => field.onChange(option.value)}
                                        isSearchable={false}
                                        styles={selectStyles}
                                    />
                                )}
                            />
                        </Field>

                        <div className="criteria-form-row">
                            <Field label="Weight" listClassName="criteria-form-shrink">
                                <input
                                    type="number"
                                    className="input full-width criteria-weight-input"
                                    {...register("weight", { required: true, valueAsNumber: true })}
                                />
                            </Field>

                            <div className="criteria-vertical-separator"></div>

                            <Field label="Color" listClassName="criteria-form-shrink">
                                <input type="color" className="criteria-color-input" {...register("color")} />
                            </Field>
                        </div>
                    </div>

                    <div className="criteria-form-section">
                        <div className="criteria-form-section-title">
                            <i className="fas fa-list-ul"></i> Detection phrases
                        </div>

                        <div className="tracker-list">
                            {trackerFields.map((field, index) => (
                                <div className="tracker-row" key={field.id}>
                                    <span className="tracker-row-handle">{index + 1}</span>
                                    <TextInput
                                        className="full-width tracker-row-input"
                                        placeholder="e.g. can I speak to your manager"
                                        {...register(`trackers.${index}.value`)}
                                    />
                                    <i
                                        className="fas fa-trash tracker-row-remove"
                                        onClick={() => onClickRemoveTracker(index)}
                                        title="Remove this tracker"
                                    ></i>
                                </div>
                            ))}
                        </div>

                        <Button
                            type="button"
                            className="validate tracker-add-button"
                            label="+ Add tracker"
                            onClick={() => appendTracker({ value: "" })}
                        />

                        <p className="criteria-form-hint">
                            Each tracker is a phrase or expression used to detect this criteria in the transcript.
                        </p>
                    </div>

                </Form.Body>
                <Form.Buttons>
                    <Button type="submit" label="Save" className="validate" disabled={!isValid} />
                </Form.Buttons>
            </Form>
        </SideModal>
    );
});

export default CriteriaDefinitionModal;
